import { memo } from 'react';
import type { SkeletonLoaderProps } from './types/screenState.types.js';

const pulse = 'animate-pulse bg-[var(--color-an-surface-hover)] rounded';

const KPI_SKELETON_KEYS = ['kpi-sk-1', 'kpi-sk-2', 'kpi-sk-3', 'kpi-sk-4'] as const;
const CHART_BAR_KEYS = ['bar-sk-1', 'bar-sk-2', 'bar-sk-3', 'bar-sk-4', 'bar-sk-5', 'bar-sk-6', 'bar-sk-7', 'bar-sk-8', 'bar-sk-9', 'bar-sk-10'] as const;
const TABLE_HEADER_KEYS = ['th-sk-1', 'th-sk-2', 'th-sk-3', 'th-sk-4', 'th-sk-5'] as const;

function tableRowKeys(count: number): string[] {
  return Array.from({ length: count }, (_, i) => `row-sk-${i + 1}`);
}

function tableCellKeys(rowKey: string): string[] {
  return TABLE_HEADER_KEYS.map((headerKey) => `${rowKey}-${headerKey}`);
}

function listItemKeys(count: number): string[] {
  return Array.from({ length: count }, (_, i) => `list-sk-${i + 1}`);
}

/**
 * Production-grade skeleton loader components for analytics charts
 * Provides better perceived performance than spinners
 */
export const SkeletonLoader = memo<SkeletonLoaderProps>(({ type, count = 1 }) => {
  if (type === 'kpi') {
    return (
      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
        {KPI_SKELETON_KEYS.map((key) => (
          <div key={key} className="flex items-center gap-3.5 p-5 rounded-[14px] bg-[var(--color-an-surface)] border-2 border-[var(--color-an-border)]">
            <div className={`w-9 h-9 rounded-lg ${pulse}`} />
            <div className="flex-1 flex flex-col gap-2">
              <div className={`h-3 w-20 ${pulse}`} />
              <div className={`h-6 w-[120px] ${pulse}`} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'chart') {
    return (
      <div className="bg-[var(--color-an-surface)] border-2 border-[var(--color-an-border)] rounded-[14px] p-6">
        <div className="mb-5">
          <div className={`h-5 w-[200px] mb-2 ${pulse}`} />
          <div className={`h-3 w-[150px] ${pulse}`} />
        </div>
        <div className="min-h-[200px] flex items-end gap-2 px-2">
          {CHART_BAR_KEYS.map((key, idx) => (
            <div key={key} className={`flex-1 rounded-t ${pulse}`} style={{ height: `${30 + (idx % 5) * 12}%` }} />
          ))}
        </div>
      </div>
    );
  }

  if (type === 'table') {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          {TABLE_HEADER_KEYS.map((key) => (
            <div key={key} className={`h-4 flex-1 ${pulse}`} />
          ))}
        </div>
        <div className="flex flex-col gap-2">
          {tableRowKeys(count).map((rowKey) => (
            <div key={rowKey} className="flex gap-2">
              {tableCellKeys(rowKey).map((cellKey) => (
                <div key={cellKey} className={`h-8 flex-1 ${pulse}`} />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'list') {
    return (
      <div className="flex flex-col gap-3">
        {listItemKeys(count).map((key) => (
          <div key={key} className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full shrink-0 ${pulse}`} />
            <div className="flex-1 flex flex-col gap-2">
              <div className={`h-4 w-3/5 ${pulse}`} />
              <div className={`h-3 w-2/5 ${pulse}`} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === 'card') {
    const cardSlotKeys = ['primary', 'status', 'key', 'secondary', 'actions'] as const;
    const slotBarClass = (slot: (typeof cardSlotKeys)[number]): string => {
      if (slot === 'primary') {
        return `h-5 w-2/5 ${pulse}`;
      }
      if (slot === 'status') {
        return `h-4 w-16 self-end ${pulse}`;
      }
      if (slot === 'actions') {
        return `ml-auto h-8 w-8 shrink-0 ${pulse}`;
      }
      return `h-3 w-3/5 ${pulse}`;
    };
    return (
      <div className="grid gap-3" data-testid="skeleton-card-grid">
        {listItemKeys(count).map((key) => (
          <div
            key={key}
            className="flex min-h-[145px] flex-col gap-2 rounded-xl border border-[var(--color-rail-card-border)] bg-[var(--color-an-surface)] p-3"
            data-testid="skeleton-card"
            data-skeleton-anatomy="pattern-a"
          >
            {cardSlotKeys.map((slot) => (
              <div
                key={`${key}-${slot}`}
                data-mobile-card-slot={slot}
                className={slotBarClass(slot)}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return null;
});

SkeletonLoader.displayName = 'SkeletonLoader';
